import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, Plus, X, Calendar as CalendarIcon, RefreshCw, Trash2, Settings } from 'lucide-react'
import { calendarAPI, widgetAPI } from '../../services/api'
import { useModalClose } from '../../hooks/useModalClose'
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, startOfWeek, endOfWeek, addWeeks, subWeeks } from 'date-fns'
import toast from 'react-hot-toast'

interface CalendarWidgetProps {
  widget: any
  isExpanded: boolean
}

export default function CalendarWidget({ widget, isExpanded }: CalendarWidgetProps) {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [view, setView] = useState<'day' | 'week' | 'month'>(() => {
    // Load saved view preference, or default based on expanded state
    return widget.config?.preferredView || (isExpanded ? 'month' : 'day')
  })
  const [showAddEvent, setShowAddEvent] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [showHolidays, setShowHolidays] = useState(widget.config?.showHolidays ?? false)
  const [syncing, setSyncing] = useState<string | null>(null)
  const [selectedEvent, setSelectedEvent] = useState<any>(null)
  const [editingEvent, setEditingEvent] = useState<any>(null)
  
  // Modal close handlers
  useModalClose(() => setShowAddEvent(false))
  useModalClose(() => setSelectedEvent(null))
  useModalClose(() => setEditingEvent(null))
  useModalClose(() => setShowSettings(false))
  const [newEvent, setNewEvent] = useState({
    title: '',
    start_time: format(currentDate, "yyyy-MM-dd'T'HH:mm"),
    end_time: format(currentDate, "yyyy-MM-dd'T'HH:mm"),
    location: '',
  })
  const queryClient = useQueryClient()
  
  // Save view preference whenever it changes
  const handleViewChange = async (newView: 'day' | 'week' | 'month') => {
    setView(newView)
    
    try {
      const newConfig = {
        ...widget.config,
        preferredView: newView
      }
      await widgetAPI.update(widget.id, {
        config_data: JSON.stringify(newConfig)
      })
      // Update local widget config
      widget.config = newConfig
    } catch (error) {
      console.error('Failed to save view preference:', error)
    }
  }

  // Fetch connected calendars
  const { data: connections } = useQuery({
    queryKey: ['calendar-connections'],
    queryFn: async () => {
      const { data } = await calendarAPI.getConnections()
      return data
    },
  })

  const monthStart = startOfMonth(currentDate)
  const monthEnd = endOfMonth(currentDate)

  const { data: events } = useQuery({
    queryKey: ['calendar-events', format(monthStart, 'yyyy-MM-dd'), format(monthEnd, 'yyyy-MM-dd')],
    queryFn: async () => {
      const { data } = await calendarAPI.getEvents(
        format(monthStart, 'yyyy-MM-dd'),
        format(monthEnd, 'yyyy-MM-dd')
      )
      return data
    },
  })

  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd })

  const handleAddEvent = async () => {
    if (!newEvent.title) {
      toast.error('Event title is required')
      return
    }

    try {
      console.log('Creating event:', newEvent)
      const response = await calendarAPI.createEvent(newEvent)
      console.log('Event created:', response)
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] })
      toast.success('Event added!')
      setShowAddEvent(false)
      setNewEvent({
        title: '',
        start_time: format(currentDate, "yyyy-MM-dd'T'HH:mm"),
        end_time: format(currentDate, "yyyy-MM-dd'T'HH:mm"),
        location: '',
      })
    } catch (error: any) {
      console.error('Failed to add event:', error)
      console.error('Error response:', error.response?.data)
      toast.error(error.response?.data?.error || 'Failed to add event')
    }
  }

  const handleUpdateEvent = async (eventId: number, data: any) => {
    try {
      await calendarAPI.updateEvent(eventId, data)
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] })
      toast.success('Event updated!')
      setEditingEvent(null)
      setSelectedEvent(null)
    } catch (error: any) {
      console.error('Failed to update event:', error)
      toast.error(error.response?.data?.error || 'Failed to update event')
    }
  }

  const handleDeleteEvent = async (eventId: number) => {
    if (!confirm('Are you sure you want to delete this event?')) return
    
    try {
      await calendarAPI.deleteEvent(eventId)
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] })
      toast.success('Event deleted!')
      setSelectedEvent(null)
    } catch (error: any) {
      console.error('Failed to delete event:', error)
      toast.error(error.response?.data?.error || 'Failed to delete event')
    }
  }

  const handleGoogleSync = async () => {
    try {
      const googleConnection = connections?.find((c: any) => c.provider === 'google')
      
      if (googleConnection) {
        // Already connected, just sync
        setSyncing('google')
        await calendarAPI.syncCalendar('google')
        queryClient.invalidateQueries({ queryKey: ['calendar-events'] })
        toast.success('Google Calendar synced successfully!')
        setSyncing(null)
      } else {
        // Need to authenticate
        const { data } = await calendarAPI.getGoogleAuthUrl()
        window.location.href = data.auth_url
      }
    } catch (error: any) {
      console.error('Google sync failed:', error)
      const errorMsg = error.response?.data?.error || 'Failed to sync Google Calendar'
      
      // Provide helpful message if OAuth is not configured
      if (errorMsg.includes('not configured')) {
        toast.error(
          'Google Calendar sync not configured. Please add GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET to your .env file.',
          { duration: 6000 }
        )
      } else {
        toast.error(errorMsg)
      }
      setSyncing(null)
    }
  }

  const handleOutlookSync = async () => {
    try {
      const outlookConnection = connections?.find((c: any) => c.provider === 'outlook')
      
      if (outlookConnection) {
        // Already connected, just sync
        setSyncing('outlook')
        await calendarAPI.syncCalendar('outlook')
        queryClient.invalidateQueries({ queryKey: ['calendar-events'] })
        toast.success('Outlook Calendar synced successfully!')
        setSyncing(null)
      } else {
        // Need to authenticate
        const { data} = await calendarAPI.getOutlookAuthUrl()
        window.location.href = data.auth_url
      }
    } catch (error: any) {
      console.error('Outlook sync failed:', error)
      const errorMsg = error.response?.data?.error || 'Failed to sync Outlook Calendar'
      
      // Provide helpful message if OAuth is not configured
      if (errorMsg.includes('not configured')) {
        toast.error(
          'Outlook Calendar sync not configured. Please add OUTLOOK_CLIENT_ID and OUTLOOK_CLIENT_SECRET to your .env file.',
          { duration: 6000 }
        )
      } else {
        toast.error(errorMsg)
      }
      setSyncing(null)
    }
  }

  const handleDisconnect = async (provider: string) => {
    if (!confirm(`Disconnect ${provider === 'google' ? 'Google' : 'Outlook'} Calendar?`)) return
    
    try {
      await calendarAPI.disconnectCalendar(provider)
      queryClient.invalidateQueries({ queryKey: ['calendar-connections'] })
      toast.success('Calendar disconnected')
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to disconnect calendar')
    }
  }

  // Generate US Holidays for a given year
  const getUSHolidays = (year: number) => {
    // Helper function to get nth weekday of month (e.g., 2nd Monday)
    const getNthWeekdayOfMonth = (year: number, month: number, weekday: number, n: number) => {
      const date = new Date(year, month, 1)
      let count = 0
      while (date.getMonth() === month) {
        if (date.getDay() === weekday) {
          count++
          if (count === n) return new Date(date)
        }
        date.setDate(date.getDate() + 1)
      }
      return date
    }

    // Helper function to get last weekday of month
    const getLastWeekdayOfMonth = (year: number, month: number, weekday: number) => {
      const date = new Date(year, month + 1, 0) // Last day of month
      while (date.getDay() !== weekday) {
        date.setDate(date.getDate() - 1)
      }
      return date
    }

    const holidays = [
      { title: "🎆 New Year's Day", date: new Date(year, 0, 1) },
      { title: "🎖️ Martin Luther King Jr. Day", date: getNthWeekdayOfMonth(year, 0, 1, 3) }, // 3rd Monday in January
      { title: "🇺🇸 Presidents' Day", date: getNthWeekdayOfMonth(year, 1, 1, 3) }, // 3rd Monday in February
      { title: "🪦 Memorial Day", date: getLastWeekdayOfMonth(year, 4, 1) }, // Last Monday in May
      { title: "🎉 Independence Day", date: new Date(year, 6, 4) },
      { title: "💼 Labor Day", date: getNthWeekdayOfMonth(year, 8, 1, 1) }, // 1st Monday in September
      { title: "🍂 Columbus Day", date: getNthWeekdayOfMonth(year, 9, 1, 2) }, // 2nd Monday in October
      { title: "🎖️ Veterans Day", date: new Date(year, 10, 11) },
      { title: "🦃 Thanksgiving", date: getNthWeekdayOfMonth(year, 10, 4, 4) }, // 4th Thursday in November
      { title: "🎄 Christmas Day", date: new Date(year, 11, 25) },
    ]

    return holidays
  }

  // Get holidays for current view
  const currentHolidays = showHolidays ? getUSHolidays(currentDate.getFullYear()) : []

  // Check if a date is a holiday
  const getHolidayForDate = (date: Date) => {
    return currentHolidays.find(holiday => 
      isSameDay(holiday.date, date)
    )
  }

  // Save settings to widget config
  const handleSaveSettings = async () => {
    try {
      const newConfig = {
        ...widget.config,
        showHolidays
      }
      await widgetAPI.update(widget.id, {
        config_data: JSON.stringify(newConfig)
      })
      queryClient.invalidateQueries({ queryKey: ['widgets'] })
      toast.success('Settings saved!')
      setShowSettings(false)
    } catch (error) {
      toast.error('Failed to save settings')
    }
  }

  // Handle OAuth callback success
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('calendar_sync') === 'success') {
      const provider = params.get('provider')
      toast.success(`${provider === 'google' ? 'Google' : 'Outlook'} Calendar connected!`)
      queryClient.invalidateQueries({ queryKey: ['calendar-connections'] })
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [])

  const renderDayView = () => {
    const todayEvents = events?.filter((event: any) =>
      isSameDay(new Date(event.start_time), currentDate)
    ) || []
    
    const todayHoliday = getHolidayForDate(currentDate)

    return (
      <div className="space-y-4">
        <div className="text-center mb-4">
          <h3 className="text-2xl font-bold">{format(currentDate, 'EEEE, MMMM d')}</h3>
          {todayHoliday && (
            <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-red-50 border border-red-200 rounded-full text-sm text-red-700">
              {todayHoliday.title}
            </div>
          )}
        </div>

        {todayEvents.length > 0 ? (
          <div className="space-y-2">
            {todayEvents.map((event: any) => (
              <div
                key={event.id}
                onClick={() => setSelectedEvent(event)}
                className="p-3 bg-primary-50 border-l-4 border-primary-600 rounded cursor-pointer hover:bg-primary-100 transition"
              >
                <div className="font-semibold text-gray-900">{event.title}</div>
                <div className="text-sm text-gray-600">
                  {format(new Date(event.start_time), 'h:mm a')} -{' '}
                  {format(new Date(event.end_time), 'h:mm a')}
                </div>
                {event.location && (
                  <div className="text-sm text-gray-600 mt-1">📍 {event.location}</div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">No events today</div>
        )}
      </div>
    )
  }

  const renderWeekView = () => {
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 })
    const weekEnd = endOfWeek(currentDate, { weekStartsOn: 0 })
    const daysInWeek = eachDayOfInterval({ start: weekStart, end: weekEnd })
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

    return (
      <div className="overflow-x-auto">
        <div className="min-w-full">
          <div className="grid grid-cols-7 gap-2 mb-2">
            {weekDays.map((dayName, idx) => {
              const dayDate = daysInWeek[idx]
              const dayHoliday = getHolidayForDate(dayDate)
              const isToday = isSameDay(dayDate, new Date())
              
              return (
                <div key={dayName} className="text-center">
                  <div className="text-xs font-semibold" style={{ color: 'var(--color-text-secondary)' }}>
                    {dayName}
                  </div>
                  <div className={`text-sm font-bold mt-1 ${
                    isToday
                      ? 'bg-primary-600 text-white rounded-full w-8 h-8 flex items-center justify-center mx-auto' 
                      : dayHoliday
                      ? 'text-red-600'
                      : ''
                  }`}
                  style={!isToday && !dayHoliday ? { color: 'var(--color-text-primary)' } : {}}>
                    {format(dayDate, 'd')}
                  </div>
                  {dayHoliday && !isToday && (
                    <div className="text-[8px] text-red-600 mt-0.5 truncate" title={dayHoliday.title}>
                      {dayHoliday.title.split(' ')[0]}
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          <div className="grid grid-cols-7 gap-2">
            {daysInWeek.map((day) => {
              const dayEvents = events?.filter((event: any) =>
                isSameDay(new Date(event.start_time), day)
              ) || []
              
              const holiday = getHolidayForDate(day)

              return (
                <div
                  key={day.toString()}
                  className={`min-h-[120px] border-2 rounded-lg p-2 hover:border-primary-300 transition ${
                    holiday ? 'bg-red-50 border-red-200' : 'border-gray-200'
                  }`}
                >
                  {/* Holiday Badge */}
                  {holiday && (
                    <div className="text-[10px] leading-tight mb-1 px-1 py-0.5 bg-red-100 text-red-700 rounded truncate font-medium">
                      {holiday.title.replace(/[^\w\s]/g, '').trim()}
                    </div>
                  )}
                  
                  {/* Events */}
                  {dayEvents.length > 0 ? (
                    <div className="space-y-1">
                      {dayEvents.slice(0, 3).map((event: any) => (
                        <div
                          key={event.id}
                          onClick={() => setSelectedEvent(event)}
                          className="text-xs p-1 rounded truncate cursor-pointer hover:bg-primary-200 transition"
                          style={{
                            backgroundColor: 'var(--color-primary-100)',
                            color: 'var(--color-primary-700)'
                          }}
                          title={event.title}
                        >
                          <div className="font-medium truncate">{event.title}</div>
                          <div className="text-[10px]">
                            {format(new Date(event.start_time), 'h:mm a')}
                          </div>
                        </div>
                      ))}
                      {dayEvents.length > 3 && (
                        <div className="text-[10px] text-center" style={{ color: 'var(--color-text-secondary)' }}>
                          +{dayEvents.length - 3} more
                        </div>
                      )}
                    </div>
                  ) : !holiday && (
                    <div className="text-xs text-center mt-8" style={{ color: 'var(--color-text-secondary)' }}>No events</div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  const renderMonthView = () => {
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

    return (
      <div>
        <div className="grid grid-cols-7 gap-1 mb-2">
          {weekDays.map((day) => (
            <div 
              key={day} 
              className="text-center text-xs font-semibold py-2"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-2">
          {daysInMonth.map((day) => {
            const dayEvents = events?.filter((event: any) =>
              isSameDay(new Date(event.start_time), day)
            ) || []

            const holiday = getHolidayForDate(day)
            const isToday = isSameDay(day, new Date())

            return (
              <div
                key={day.toString()}
                onClick={() => {
                  setCurrentDate(day)
                  handleViewChange('day')
                }}
                className={`relative min-h-[100px] p-1 rounded-lg text-sm transition cursor-pointer border-2 ${
                  holiday
                    ? 'bg-red-50 hover:bg-red-100 border-red-200'
                    : isToday
                    ? 'border-primary-600'
                    : isSameMonth(day, currentDate)
                    ? 'border-transparent hover:border-gray-300'
                    : 'border-transparent'
                }`}
                style={{
                  backgroundColor: isToday
                    ? 'var(--color-primary-100)'
                    : holiday
                    ? undefined
                    : isSameMonth(day, currentDate)
                    ? 'var(--color-surface)'
                    : 'var(--color-background)',
                  borderColor: isToday
                    ? 'var(--color-primary)'
                    : undefined
                }}
              >
                <div className={`text-xs font-semibold mb-1`}
                  style={{
                    color: isToday
                      ? 'var(--color-primary)'
                      : isSameMonth(day, currentDate)
                      ? 'var(--color-text-primary)'
                      : 'var(--color-text-secondary)'
                  }}
                >
                  {format(day, 'd')}
                </div>
                
                {holiday && (
                  <div className="text-[9px] leading-tight mb-1 px-1 py-0.5 bg-red-100 text-red-700 rounded truncate">
                    {holiday.title.replace(/[^\w\s]/g, '').trim()}
                  </div>
                )}
                
                <div className="space-y-0.5">
                  {dayEvents.slice(0, 3).map((event: any, idx: number) => (
                    <div
                      key={event.id}
                      className="text-[9px] leading-tight px-1 py-0.5 rounded truncate"
                      style={{
                        backgroundColor: 'var(--color-primary-100)',
                        color: 'var(--color-primary-700)'
                      }}
                      title={event.title}
                    >
                      {event.title}
                    </div>
                  ))}
                  {dayEvents.length > 3 && (
                    <div className="text-[8px] text-center" style={{ color: 'var(--color-text-secondary)' }}>
                      +{dayEvents.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  const handlePrevious = () => {
    if (view === 'day') {
      setCurrentDate(new Date(currentDate.getTime() - 24 * 60 * 60 * 1000))
    } else if (view === 'week') {
      setCurrentDate(subWeeks(currentDate, 1))
    } else {
      setCurrentDate(subMonths(currentDate, 1))
    }
  }

  const handleNext = () => {
    if (view === 'day') {
      setCurrentDate(new Date(currentDate.getTime() + 24 * 60 * 60 * 1000))
    } else if (view === 'week') {
      setCurrentDate(addWeeks(currentDate, 1))
    } else {
      setCurrentDate(addMonths(currentDate, 1))
    }
  }

  const getHeaderTitle = () => {
    if (view === 'day') {
      return format(currentDate, 'MMMM d, yyyy')
    } else if (view === 'week') {
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 })
      const weekEnd = endOfWeek(currentDate, { weekStartsOn: 0 })
      return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`
    } else {
      return format(currentDate, 'MMMM yyyy')
    }
  }

  const cycleView = () => {
    if (view === 'day') handleViewChange('week')
    else if (view === 'week') handleViewChange('month')
    else handleViewChange('day')
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrevious}
            className="p-2 hover:bg-gray-100 rounded transition"
          >
            <ChevronLeft size={20} />
          </button>
          <h3 className="text-lg font-semibold min-w-[200px] text-center">{getHeaderTitle()}</h3>
          <button
            onClick={handleNext}
            className="p-2 hover:bg-gray-100 rounded transition"
          >
            <ChevronRight size={20} />
          </button>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="p-2 hover:bg-gray-100 rounded transition"
            title="Settings"
          >
            <Settings size={18} />
          </button>
          <button
            onClick={cycleView}
            className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition min-w-[60px]"
            title="Switch view"
          >
            {view === 'day' ? 'Day' : view === 'week' ? 'Week' : 'Month'}
          </button>
          <button 
            onClick={() => setShowAddEvent(true)}
            className="p-2 bg-primary-600 text-white hover:bg-primary-700 rounded transition"
            title="Add event"
          >
            <Plus size={20} />
          </button>
        </div>
      </div>


      {/* Settings Modal */}
      {showSettings && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowSettings(false)}
        >
          <div 
            className="rounded-lg shadow-2xl w-full max-w-md mx-4"
            style={{ 
              backgroundColor: 'var(--color-surface)',
              color: 'var(--color-text-primary)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: 'var(--color-border)' }}>
              <h3 className="text-xl font-semibold">Calendar Settings</h3>
              <button
                onClick={() => setShowSettings(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Show US Holidays Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-base font-medium">Show US Holidays</div>
                  <div className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                    Display federal holidays on calendar
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showHolidays}
                    onChange={(e) => setShowHolidays(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>

              {/* Calendar Sync Section */}
              <div className="border-t pt-6" style={{ borderColor: 'var(--color-border)' }}>
                <h4 className="text-lg font-medium mb-4" style={{ color: 'var(--color-text-primary)' }}>
                  Calendar Sync
                </h4>

                {/* Google Calendar */}
                {(() => {
                  const googleConnection = connections?.find((c: any) => c.provider === 'google')
                  return googleConnection ? (
                    <div className="p-4 border rounded-lg mb-3" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)' }}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <svg className="w-6 h-6" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                          </svg>
                          <div>
                            <div className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>Google Calendar</div>
                            <div className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{googleConnection.provider_email}</div>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={handleGoogleSync}
                          disabled={syncing === 'google'}
                          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm rounded transition"
                          style={{
                            backgroundColor: 'var(--color-primary)',
                            color: 'white'
                          }}
                        >
                          <RefreshCw size={14} className={syncing === 'google' ? 'animate-spin' : ''} />
                          {syncing === 'google' ? 'Syncing...' : 'Sync Now'}
                        </button>
                        <button
                          onClick={() => handleDisconnect('google')}
                          className="px-3 py-2 text-sm rounded transition"
                          style={{
                            backgroundColor: 'var(--color-border)',
                            color: 'var(--color-text-primary)'
                          }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={handleGoogleSync}
                      className="w-full flex items-center gap-3 p-4 border rounded-lg hover:bg-gray-50 transition mb-3"
                      style={{
                        borderColor: 'var(--color-border)',
                        backgroundColor: 'var(--color-surface)',
                        color: 'var(--color-text-primary)'
                      }}
                    >
                      <svg className="w-6 h-6" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      <span>Connect Google Calendar</span>
                    </button>
                  )
                })()}

                {/* Outlook Calendar */}
                {(() => {
                  const outlookConnection = connections?.find((c: any) => c.provider === 'outlook')
                  return outlookConnection ? (
                    <div className="p-4 border rounded-lg mb-3" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)' }}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="#0078D4">
                            <path d="M24 7.387v9.226a3.387 3.387 0 01-3.387 3.387h-7.226V4h7.226A3.387 3.387 0 0124 7.387z"/>
                            <path opacity=".5" d="M13.387 4v16H3.387A3.387 3.387 0 010 16.613V7.387A3.387 3.387 0 013.387 4z"/>
                            <circle cx="6.774" cy="12" r="3.774" fill="#fff"/>
                          </svg>
                          <div>
                            <div className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>Outlook Calendar</div>
                            <div className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{outlookConnection.provider_email}</div>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={handleOutlookSync}
                          disabled={syncing === 'outlook'}
                          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm rounded transition"
                          style={{
                            backgroundColor: 'var(--color-primary)',
                            color: 'white'
                          }}
                        >
                          <RefreshCw size={14} className={syncing === 'outlook' ? 'animate-spin' : ''} />
                          {syncing === 'outlook' ? 'Syncing...' : 'Sync Now'}
                        </button>
                        <button
                          onClick={() => handleDisconnect('outlook')}
                          className="px-3 py-2 text-sm rounded transition"
                          style={{
                            backgroundColor: 'var(--color-border)',
                            color: 'var(--color-text-primary)'
                          }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={handleOutlookSync}
                      className="w-full flex items-center gap-3 p-4 border rounded-lg hover:bg-gray-50 transition"
                      style={{
                        borderColor: 'var(--color-border)',
                        backgroundColor: 'var(--color-surface)',
                        color: 'var(--color-text-primary)'
                      }}
                    >
                      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="#0078D4">
                        <path d="M24 7.387v9.226a3.387 3.387 0 01-3.387 3.387h-7.226V4h7.226A3.387 3.387 0 0124 7.387z"/>
                        <path opacity=".5" d="M13.387 4v16H3.387A3.387 3.387 0 010 16.613V7.387A3.387 3.387 0 013.387 4z"/>
                        <circle cx="6.774" cy="12" r="3.774" fill="#fff"/>
                      </svg>
                      <span>Connect Outlook Calendar</span>
                    </button>
                  )
                })()}
              </div>

              {/* Save Button */}
              <div className="flex gap-3 border-t pt-6" style={{ borderColor: 'var(--color-border)' }}>
                <button
                  onClick={() => setShowSettings(false)}
                  className="flex-1 px-4 py-2 rounded-lg transition font-medium"
                  style={{
                    backgroundColor: 'var(--color-border)',
                    color: 'var(--color-text-primary)'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      <div className="flex-1 overflow-y-auto">
        {view === 'day' ? renderDayView() : view === 'week' ? renderWeekView() : renderMonthView()}
      </div>

      {/* Add Event Modal */}
      {showAddEvent && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4" onClick={() => setShowAddEvent(false)}>
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Add Event</h3>
              <button onClick={() => setShowAddEvent(false)} className="p-2 hover:bg-gray-100 rounded-lg transition">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Event title"
                  autoFocus
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
                  <input
                    type="datetime-local"
                    value={newEvent.start_time}
                    onChange={(e) => setNewEvent({ ...newEvent, start_time: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Time</label>
                  <input
                    type="datetime-local"
                    value={newEvent.end_time}
                    onChange={(e) => setNewEvent({ ...newEvent, end_time: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                <input
                  type="text"
                  value={newEvent.location}
                  onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Event location (optional)"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleAddEvent}
                className="flex-1 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition font-semibold"
              >
                Add Event
              </button>
              <button
                onClick={() => setShowAddEvent(false)}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Event Details Modal */}
      {selectedEvent && !editingEvent && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4" onClick={() => setSelectedEvent(null)}>
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900">{selectedEvent.title}</h3>
              <button onClick={() => setSelectedEvent(null)} className="p-2 hover:bg-gray-100 rounded-lg transition">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <div className="flex items-center gap-3 text-gray-700">
                <CalendarIcon size={20} className="text-gray-500" />
                <div>
                  <div className="font-medium">
                    {format(new Date(selectedEvent.start_time), 'EEEE, MMMM d, yyyy')}
                  </div>
                  <div className="text-sm text-gray-600">
                    {format(new Date(selectedEvent.start_time), 'h:mm a')} - {format(new Date(selectedEvent.end_time), 'h:mm a')}
                  </div>
                </div>
              </div>

              {selectedEvent.location && (
                <div className="flex items-center gap-3 text-gray-700">
                  <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>{selectedEvent.location}</span>
                </div>
              )}

              {selectedEvent.description && (
                <div className="flex gap-3 text-gray-700">
                  <svg className="w-5 h-5 text-gray-500 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                  </svg>
                  <p className="flex-1">{selectedEvent.description}</p>
                </div>
              )}

              {selectedEvent.source && selectedEvent.source !== 'internal' && (
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg text-sm">
                  <span className="text-gray-600">Synced from</span>
                  <span className="font-medium capitalize">{selectedEvent.source}</span>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              {selectedEvent.source === 'internal' && (
                <button
                  onClick={() => {
                    setEditingEvent(selectedEvent)
                    setSelectedEvent(null)
                  }}
                  className="flex-1 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition font-semibold"
                >
                  Edit Event
                </button>
              )}
              {selectedEvent.source === 'internal' && (
                <button
                  onClick={() => handleDeleteEvent(selectedEvent.id)}
                  className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-semibold"
                >
                  Delete
                </button>
              )}
              {selectedEvent.source !== 'internal' && (
                <div className="flex-1 text-center text-sm text-gray-500 py-3">
                  This event is synced from {selectedEvent.source} and cannot be edited here.
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Event Edit Modal */}
      {editingEvent && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4" onClick={() => setEditingEvent(null)}>
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Edit Event</h3>
              <button onClick={() => setEditingEvent(null)} className="p-2 hover:bg-gray-100 rounded-lg transition">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editingEvent.title}
                  onChange={(e) => setEditingEvent({ ...editingEvent, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Event title"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
                  <input
                    type="datetime-local"
                    value={format(new Date(editingEvent.start_time), "yyyy-MM-dd'T'HH:mm")}
                    onChange={(e) => setEditingEvent({ ...editingEvent, start_time: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Time</label>
                  <input
                    type="datetime-local"
                    value={format(new Date(editingEvent.end_time), "yyyy-MM-dd'T'HH:mm")}
                    onChange={(e) => setEditingEvent({ ...editingEvent, end_time: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                <input
                  type="text"
                  value={editingEvent.location || ''}
                  onChange={(e) => setEditingEvent({ ...editingEvent, location: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Event location (optional)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={editingEvent.description || ''}
                  onChange={(e) => setEditingEvent({ ...editingEvent, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Event description (optional)"
                  rows={3}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  if (!editingEvent.title) {
                    toast.error('Event title is required')
                    return
                  }
                  handleUpdateEvent(editingEvent.id, {
                    title: editingEvent.title,
                    start_time: editingEvent.start_time,
                    end_time: editingEvent.end_time,
                    location: editingEvent.location || null,
                    description: editingEvent.description || null,
                  })
                }}
                className="flex-1 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition font-semibold"
              >
                Save Changes
              </button>
              <button
                onClick={() => setEditingEvent(null)}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

